import { useEffect } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';

import { useInstallGuide } from 'src/hooks/use-install-guide';

import {
  promptInstall,
  detectPlatform,
  openInstallGuide,
  closeInstallGuide,
  snoozeInstallGuide,
  shouldAutoShowInstallGuide,
  dismissInstallGuideForever,
} from 'src/utils/pwa-install';

import { useTranslate } from 'src/locales';

import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';

// ----------------------------------------------------------------------

const AUTO_OPEN_DELAY_MS = 2000;

// DashboardLayout si rimonta tra gruppi di route: apertura automatica una volta per sessione
let autoOpenChecked = false;

const STEPS = {
  'ios-safari': [
    { icon: 'material-symbols:ios-share', key: 'ios_share_bottom' },
    { icon: 'material-symbols:add-box-outline', key: 'ios_add_home' },
    { icon: 'material-symbols:check-circle-outline', key: 'ios_confirm' },
  ],
  'ios-other': [
    { icon: 'material-symbols:ios-share', key: 'ios_share_top' },
    { icon: 'material-symbols:add-box-outline', key: 'ios_add_home' },
    { icon: 'material-symbols:check-circle-outline', key: 'ios_confirm' },
  ],
  'android-chrome': [
    { icon: 'material-symbols:more-vert', key: 'android_menu' },
    { icon: 'material-symbols:install-mobile', key: 'android_install' },
    { icon: 'material-symbols:check-circle-outline', key: 'android_confirm' },
  ],
  'android-other': [
    { icon: 'material-symbols:menu', key: 'other_menu' },
    { icon: 'material-symbols:install-mobile', key: 'android_install' },
    { icon: 'material-symbols:check-circle-outline', key: 'android_confirm' },
  ],
};

// ----------------------------------------------------------------------

export default function InstallGuideSheet() {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { open, promptAvailable } = useInstallGuide();

  const platform = detectPlatform();
  const auto = open === 'auto';

  useEffect(() => {
    if (autoOpenChecked) return undefined;
    const timer = setTimeout(() => {
      autoOpenChecked = true;
      if (shouldAutoShowInstallGuide()) openInstallGuide('auto');
    }, AUTO_OPEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // Chiusura senza scelta (tap fuori, swipe) dalla proposta automatica = "Più tardi"
  const handleClose = () => {
    if (auto) snoozeInstallGuide();
    closeInstallGuide();
  };

  const handleNeverAgain = () => {
    dismissInstallGuideForever();
    closeInstallGuide();
  };

  const handleInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') {
      closeInstallGuide();
      enqueueSnackbar(t('install_guide.installed'), { variant: 'success' });
    }
    // 'dismissed': la guida resta aperta e mostra i passi manuali
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      enqueueSnackbar(t('install_guide.link_copied'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('install_guide.link_copy_failed', { url: window.location.origin }), { variant: 'warning' });
    }
  };

  const renderSteps = (
    <Stack spacing={2} sx={{ mt: 3 }}>
      {(STEPS[platform] || []).map((step, index) => (
        <Stack key={step.key} direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.lighter',
              color: 'primary.main',
            }}
          >
            <Iconify icon={step.icon} width={22} />
          </Box>
          <Typography variant="body2">
            <Box component="span" sx={{ fontWeight: 'fontWeightBold' }}>
              {index + 1}.{' '}
            </Box>
            {t(`install_guide.steps.${step.key}`)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  const renderInApp = (
    <Stack spacing={2} sx={{ mt: 3 }}>
      <Typography variant="body2">{t('install_guide.in_app')}</Typography>
      <Button
        variant="outlined"
        startIcon={<Iconify icon="material-symbols:content-copy-outline" />}
        onClick={handleCopyLink}
      >
        {t('install_guide.copy_link')}
      </Button>
    </Stack>
  );

  const renderInstallButton = (
    <Button
      fullWidth
      size="large"
      variant="contained"
      sx={{ mt: 3 }}
      startIcon={<Iconify icon="material-symbols:install-mobile" />}
      onClick={handleInstall}
    >
      {t('install_guide.install')}
    </Button>
  );

  let body = renderSteps;
  if (platform === 'in-app') body = renderInApp;
  else if (promptAvailable) body = renderInstallButton;

  return (
    <Drawer
      anchor="bottom"
      open={!!open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          px: 3,
          pt: 3,
          pb: 'calc(env(safe-area-inset-bottom) + 16px)',
          maxWidth: 600,
          mx: 'auto',
        },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          component="img"
          src="/apple-touch-icon.png"
          alt="HeyJob"
          sx={{ width: 56, height: 56, borderRadius: 1.5, flexShrink: 0 }}
        />
        <Box>
          <Typography variant="h6">{t('install_guide.title')}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('install_guide.subtitle')}
          </Typography>
        </Box>
      </Stack>

      {body}

      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 3 }}>
        {auto ? (
          <>
            <Button color="inherit" onClick={handleNeverAgain}>
              {t('install_guide.never')}
            </Button>
            <Button color="inherit" variant="outlined" onClick={handleClose}>
              {t('install_guide.later')}
            </Button>
          </>
        ) : (
          <Button color="inherit" variant="outlined" onClick={handleClose}>
            {t('install_guide.close')}
          </Button>
        )}
      </Stack>
    </Drawer>
  );
}
